import type { NetMessage } from './transport';
import { dungeonService } from '../services/DungeonService';
import { DirectionEnum } from '../types/enums';
import { getTargetPosition } from '../utils/map.utils';
import { spellExecutor } from '../services/SpellService';
import { netService } from '../services/NetService';
import { reconnectRegistry } from './reconnect';
import { sendCastRejected } from './combatEvents';
import { SPELL_REGISTRY } from '../data/spells.config';

export interface MoveIntent extends NetMessage {
  type: 'move-intent';
  dir: number;
  seq: number;
}
export interface FaceIntent extends NetMessage {
  type: 'face-intent';
  dir: number;
}
export interface AttackIntent extends NetMessage {
  type: 'attack-intent';
  dir: number;
  seq: number;
}
export interface SkillIntent extends NetMessage {
  type: 'skill-intent';
  slot: number;
  dir: number;
  seq: number;
}
export interface InteractIntent extends NetMessage {
  type: 'interact-intent';
}

export type Intent =
  | MoveIntent
  | FaceIntent
  | AttackIntent
  | SkillIntent
  | InteractIntent;

const INTENT_TYPES = new Set([
  'move-intent',
  'face-intent',
  'attack-intent',
  'skill-intent',
  'interact-intent',
]);

export function isIntent(msg: NetMessage): boolean {
  return INTENT_TYPES.has(msg.type);
}

export function sendIntent(intent: Intent): void {
  netService.session?.broadcast('reliable', intent);
}

let moveSeq = 0;
let hostAckedMoveSeq = 0;
let castSeq = 0;

// Client: next monotonic cast sequence — keys a predicted preview to the host's
// later confirmation (preview-add) or refusal (cast-rejected). Per-client.
export function nextCastSeq(): number {
  castSeq += 1;
  return castSeq;
}

/** Send a move intent with a monotonic sequence number, used by the client to
 *  reconcile its predicted position only once the host has acked this input. */
export function sendMoveIntent(dir: number): void {
  moveSeq += 1;
  sendIntent({ type: 'move-intent', dir, seq: moveSeq });
}

export function lastSentMoveSeq(): number {
  return moveSeq;
}

/** Client: record the last move seq the host has processed (from snapshots). */
export function setHostAckedMoveSeq(seq: number): void {
  hostAckedMoveSeq = seq;
}

/**
 * Reset the move-seq tracking — used when the client's local player entity is
 * replaced (e.g. revived), since the new host entity restarts at lastInputSeq=0.
 * Without this, pendingMoveCount stays huge and the prediction cap blocks moves.
 */
export function resetMoveTracking(): void {
  moveSeq = 0;
  hostAckedMoveSeq = 0;
  castSeq = 0;
  dungeonService.previewService.clearPredictions();
}

/** Client: move intents sent but not yet acked by the host. Used to cap how far
 *  the prediction may run ahead, bounding desync on a rejected step. */
export function pendingMoveCount(): number {
  return moveSeq - hostAckedMoveSeq;
}

export function applyIntentOnHost(fromPeerId: string, msg: NetMessage): void {
  if (!isIntent(msg)) {
    return;
  }
  if (netService.session?.getPhase() !== 'dungeon') {
    return;
  }
  const playerId = reconnectRegistry.resolvePlayerId(fromPeerId);
  const player = dungeonService.getPlayers().find((p) => p.id === playerId);
  if (!player) {
    return;
  }
  switch (msg.type) {
    case 'move-intent': {
      // Queue rather than apply directly: the host drains one move per free tick
      // (PlayerEntity.processPendingMove), following the client's exact path and
      // acking only applied steps — so a step the host can't take yet is not
      // dropped (which would desync) and not falsely acked.
      const move = msg as MoveIntent;
      player.queueMove(move.dir as DirectionEnum, move.seq);
      return;
    }
    case 'face-intent': {
      player.face((msg as FaceIntent).dir as DirectionEnum);
      return;
    }
    case 'attack-intent': {
      const atk = msg as AttackIntent;
      const def = player.getBasicAttackSpell();
      const telegraphs = !!def.preview && !def.projectile;
      if (!player.canCast()) {
        if (telegraphs) {
          sendCastRejected({ entityId: player.id, castSeq: atk.seq });
        }
        return;
      }
      const target = getTargetPosition(
        player.positionX,
        player.positionY,
        atk.dir as DirectionEnum,
      );
      const cast = player.basicAttack(target.x, target.y, atk.seq);
      if (telegraphs && !cast) {
        sendCastRejected({ entityId: player.id, castSeq: atk.seq });
      }
      return;
    }
    case 'skill-intent': {
      const skill = msg as SkillIntent;
      const spellId = player.slots[skill.slot]?.spellId;
      const def = spellId
        ? (player.getSpellInstance(spellId) ?? SPELL_REGISTRY[spellId])
        : undefined;
      const telegraphs = !!def?.preview && !def?.projectile;
      const reject = () => {
        if (telegraphs) {
          sendCastRejected({ entityId: player.id, castSeq: skill.seq });
        }
      };
      if (!player.canCast() || !spellId) {
        reject();
        return;
      }
      const target = getTargetPosition(
        player.positionX,
        player.positionY,
        skill.dir as DirectionEnum,
      );
      const cast = spellExecutor.execute(
        player,
        spellId,
        target.x,
        target.y,
        skill.seq,
      );
      if (!cast) {
        reject();
      }
      return;
    }
    case 'interact-intent': {
      const loc =
        dungeonService.locationService.getInteractableLocation(player);
      if (loc) {
        dungeonService.locationService.interactWithLocation(loc, player);
      }
      return;
    }
  }
}
